import {
  getMarketsAnalytics,
  getOrderTransactions,
  getOverallAnalytics, getPopularAnalytics,
  getTopTradesAnalytics,
  getTradeTransactions,
  getTransactions
} from '@App/api/reports'

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { Order, Trade } from '@Redux/slices/trade/trades/types'

export interface MoneyObject {
  amount: number
  currency: string
  rate_change: number
  currency_symbol: string
  system_amount?: number
  system_currency?: string
  system_symbol?: string
  display?: boolean
}

export interface DateTimeJson {
  date: string
  timezone_type: number
  timezone: string
}

export interface TimestampInterval {
  timestampFrom: number | null
  timestampTo: number | null
}

export interface TransactionSource {
  id: number
  operation_type: string
  rate_change_id: number
  amount: MoneyObject
  operation_time: string
  operation_note: string | null
  payment_operation_params: unknown
  bet_id?: number
  trading_bet?: MiniTrade | null
}

export interface TransactionDestination {
  id: number
  operation_type: string
  amount: MoneyObject
  operation_time: DateTimeJson | string
  operation_note: string | null
  payment_operation_params: unknown
  external_id?: string | null
  bet_id?: number
  trading_bet?: MiniTrade | null
}

export interface UserWallet {
  id: number
  user_id: number
  name: string
  balance: MoneyObject & { system_amount: number; display: boolean }
  front_balance: MoneyObject
  wallet_type: string
  non_transaction: boolean
  currency_symbol: string
  update_time: DateTimeJson
  ut_ts: number
  type: string
  mirror_trade: boolean
  trading_settings: Record<string, number>
  wallet_id?: number
  bonus_instance_id?: number | null
}

export interface MiniUser {
  id: number
  username: string
  default_currency: string
  mobile_phone: string
  email: string
  language: string
  wallets: UserWallet[] | null

  registration_time: DateTimeJson
  last_activity_time: DateTimeJson
  last_login_time: DateTimeJson

  ip: string | null
  country: string
  front_id: string

  [key: string]: unknown
}

export interface MiniTrade {
  id: number
  front_id: string
  user_id: number
  wallet_id: number
  market_id: number
  action_type: 'buy' | 'sell'
  leverage: number
  status: string
  open_rate: number
  close_rate: number | null
  currency: string
  amount: number
  quantity: number
  result: number
  open_time: string
  close_time: string | null
  close_reason: string | null
  request_key: string
  type: 'trade' | 'order',
  trading_bet_order?: MiniOrderInfo | null
}

export interface MiniOrder extends MiniTrade {
  trading_bet_order: MiniOrderInfo
}

export interface MiniOrderInfo {
  limit_price: number
  cancellation_time: string | null
  execution_date: string | null
  order_type: 'stop' | 'limit'
  status: 'executed' | 'cancelled' | 'pending'
  execute_transaction_id: number | null
}

export interface MiniMarket {
  id: number
  title: string
  type: string
  source_instrument_id: string
  target_instrument_id: string
  commission: number
  rate: number
  spread_bid: number
  spread_ask: number
  enabled: boolean
  is_popular: boolean
  amount_precision: number
  price_precision: number
  source_instrument_name: string
  target_instrument_name: string
  source_instrument_code: string
  target_instrument_code: string
  symbol: string
}

export interface AnalyticsLightweightTrade {
  id: number
  front_id: string
  user_id: number
  wallet_id: number
  market_id: number
  action_type: 'buy' | 'sell'
  leverage: number
  status: string
  open_rate: number
  close_rate: number | null
  currency: string
  amount: number
  quantity: number
  result: number
  open_time: string
  close_time: string | null
  close_reason: string | null
  request_key: string
  type: 'trade' | 'order',
  trading_bet_order?: MiniOrderInfo | null
}

export interface Transaction {
  id: number
  front_id: string
  transaction_time: string // "2025-12-05 08:55:15"
  type: string
  amount: MoneyObject
  tax: string

  status: string

  source_type: string
  destination_type: string

  provider: string

  initiator_id: number
  user_id: number

  source: TransactionSource
  destination: TransactionDestination

  initiator_user: MiniUser

  ip: string | null
  note: string | null
  device_id: string | null
  approve_username: string | null

  exchange_transaction: number
  player_affiliate_id: number | null
  last_balance: number
  market: MiniMarket
  trade: null | MiniTrade
}

export interface TradeTransaction extends Transaction {
  trade: MiniTrade
}

export interface OrderTransaction extends TradeTransaction {
  trade: MiniOrder
}

export interface OverallAnalytics {
  total_trades: number,
  short_trades: number,
  long_trades: number,
  profitable_trades: number,
  total_pnl: number,
  win_rate_percent: number,
  avg_holding_all: number,
  avg_holding_profit: number,
  avg_holding_loss: number,
  avg_return_all: number,
  avg_return_profit: number,
  avg_return_loss: number,
}

export interface AnalyticsTrade extends MiniTrade {
  market: MiniMarket
}

export interface MarketGroup {
  market: MiniMarket
  total_result: number
  trades_count: number
}

const isTransaction = (transaction: unknown): transaction is Transaction => {
  return !!(transaction && typeof transaction === 'object' && 'transaction_time' in transaction)

}

const isTransactionArray = (transactions: unknown): transactions is Transaction[] => {
  if (!Array.isArray(transactions)) return false
  if (!transactions.length) return true
  return !!transactions.every(isTransaction)

}

const isTradeArray = (trades: unknown): trades is Trade[] => {
  if (!Array.isArray(trades)) return false
  return !!trades.length

}

const isOrderArray = (orders: unknown): orders is Order[] => {
  if (!Array.isArray(orders)) return false
  return !!orders.length

}

const initialState: {
  transactions: Transaction[]
  tradeTransactions: TradeTransaction[]
  orderTransactions: OrderTransaction[]
  topUserTrades: AnalyticsTrade[]
  marketsAnalytics: MarketGroup[]
  popularAnalytics: MarketGroup[]
  overallAnalytics: OverallAnalytics | null
} = {
  transactions: [],
  tradeTransactions: [],
  orderTransactions: [],
  topUserTrades: [],
  marketsAnalytics: [],
  popularAnalytics: [],
  overallAnalytics: null
}

export const asyncGetTransactions = createAsyncThunk('reports/transactions', async ({ timestampInterval }: { timestampInterval: TimestampInterval }
) => {
  const resp = await getTransactions(timestampInterval)
  if (typeof resp !== 'object' || resp == null) {
    return []
  }

  if ('data' in resp && isTransactionArray(resp.data)) {
    console.log(resp.data)
    return resp.data
  }
  return []
})

export const asyncGetTradeTransactions = createAsyncThunk('reports/tradeTransactions',   async ({ timestampInterval }: { timestampInterval: TimestampInterval }
) => {
  const resp = await getTradeTransactions(timestampInterval)

  if (typeof resp !== 'object' || resp == null) {
    return []
  }

  if ('data' in resp && isTransactionArray(resp.data)) {
    console.log(resp.data)
    return resp.data as TradeTransaction[]
  }
  return []
})

export const asyncGetOrderTransactions = createAsyncThunk('reports/orderTransactions', async ({ timestampInterval }: { timestampInterval: TimestampInterval }
) => {
  const resp = await getOrderTransactions(timestampInterval)

  if (typeof resp !== 'object' || resp == null) {
    return []
  }

  if ('data' in resp && isTransactionArray(resp.data)) {
    console.log(resp.data)
    return resp.data as OrderTransaction[]
  }
  return []
})

export const asyncGetOverallAnalytics = createAsyncThunk('reports/overallAnalytics', async (timestampInterval: TimestampInterval) => {
  const resp = await getOverallAnalytics(timestampInterval)
  if (typeof resp !== 'object' || resp == null) {
    return null
  }

  if ('data' in resp) {
    return resp.data as OverallAnalytics
  }
  return null
})

export const asyncGetTopTradesAnalytics = createAsyncThunk(
  'reports/topTradesAnalytics',
  async ({timestampInterval, profitable}: {timestampInterval: TimestampInterval, profitable: boolean }
)=> {
  const resp = await getTopTradesAnalytics(timestampInterval, profitable)
  if (typeof resp !== 'object' || resp == null) {
    return []
  }

  if ('data' in resp) {
    return resp.data as AnalyticsTrade[]
  }
  return []
})

export const asyncGetMarketsAnalytics = createAsyncThunk(
  'reports/marketsAnalytics',
  async ({timestampInterval, profitable}: {timestampInterval: TimestampInterval, profitable: boolean }
)=> {
    const resp = await getMarketsAnalytics(timestampInterval, profitable)
    if (typeof resp !== 'object' || resp == null) {
      return []
    }

    if ('data' in resp) {
      return resp.data as MarketGroup[]
    }
    return []
})

export const asyncGetPopularAnalytics = createAsyncThunk(
  'reports/popularAnalytics',
  async ({timestampInterval, sortBy}: {timestampInterval: TimestampInterval, sortBy: string}
  )=> {
    const resp = await getPopularAnalytics(timestampInterval, sortBy)
    if (typeof resp !== 'object' || resp == null) {
      return []
    }

    if ('data' in resp) {
      return resp.data as MarketGroup[]
    }
    return []
  })

export const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(asyncGetTransactions.fulfilled, (state, action) => {
        state.transactions = action.payload
      })
      .addCase(asyncGetTradeTransactions.fulfilled, (state, action) => {
        state.tradeTransactions = action.payload
      })
      .addCase(asyncGetOrderTransactions.fulfilled, (state, action) => {
        state.orderTransactions = action.payload
      })
      .addCase(asyncGetOverallAnalytics.fulfilled, (state, action) => {
        state.overallAnalytics = action.payload
      })
      .addCase(asyncGetTopTradesAnalytics.fulfilled, (state, action) => {
        state.topUserTrades = action.payload
      })
      .addCase(asyncGetMarketsAnalytics.fulfilled, (state, action) => {
        state.marketsAnalytics = action.payload
      })
      .addCase(asyncGetPopularAnalytics.fulfilled, (state, action) => {
        state.popularAnalytics = action.payload
      })
  },
})

export default reportsSlice.reducer
